import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FormlyFieldAnyComponent } from '@app/shared/components/formly-components/formly-field-any/formly-field-any.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { IonicModule } from '@ionic/angular';
import { FormlyAttributes } from "@ngx-formly/core";
import { SubSink } from 'subsink';
import { LocaleDatePipe } from '@app/pipes/locale-date.pipe';

@Component({
  selector: 'app-formly-field-date-time',
  templateUrl: './formly-field-date-time.component.html',
  styleUrls: ['./formly-field-date-time.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    IonicModule,
    FormsModule,
    CommonModule,
    TranslateModule,
    ReactiveFormsModule,
    LocaleDatePipe,
    FormlyAttributes,
],
})
export class FormlyFieldDateTimeComponent extends FormlyFieldAnyComponent implements OnInit, OnDestroy {
  public localValue:string = '';
  private subsink = new SubSink();

  constructor(private changeDetectorRef: ChangeDetectorRef) {
    super();
  }
  

  ngOnInit(): void {
    if (!['date','datetime-local','time'].includes(this.field?.props?.type || '')) {
      console.warn(`FormlyFieldDateTimeComponent: Unsupported type ${this.field?.props?.type}, defaulting to date`);
      this.field!.props!.type = 'date';
    }
    
    // Subscribe to formControl value changes to update localValue
    this.subsink.sink = this.formControl.valueChanges.subscribe(value => {
      this.updateLocalValue(value);
    });
    
    // Initialize with current value
    this.updateLocalValue(this.formControl.value);
  }

  ngOnDestroy(): void {
    this.subsink.unsubscribe();
  }
  
  private updateLocalValue(value: any): void {
    const localDateTime = value || '';
    switch (this.field?.props?.type) {
      case 'date':
        // value is in yyyy-mm-dd format
        this.localValue = localDateTime;
        break;
      case 'time':
        // value is in HH:MM:SS.XXXZ format in UTC
        if (localDateTime) {
          const date = new Date(`1970-01-01T${localDateTime}`);
          const hours = date.getUTCHours().toString().padStart(2, '0');
          const minutes = date.getUTCMinutes().toString().padStart(2, '0');
          this.localValue = `${hours}:${minutes}`;
        } else {
          this.localValue = '';
        }
        break;
      case 'datetime-local':
        if (localDateTime) {
          const date = new Date(localDateTime);
          const tzOffset = -date.getTimezoneOffset();
          const diff = tzOffset >= 0 ? '+' : '-';
          const pad = (n: number) => n < 10 ? '0' + n : n;
          const localISOTime = date.getFullYear() + '-' +
            pad(date.getMonth() + 1) + '-' +
            pad(date.getDate()) + 'T' +
            pad(date.getHours()) + ':' +
            pad(date.getMinutes());
          this.localValue = localISOTime;
        } else {
          this.localValue = '';
        }
        break;
      default:
        console.warn(`FormlyFieldDateTimeComponent: Unsupported type ${this.field?.props?.type}`);
    }
  }

  public override onChange(event: any): void {
    console.log('formlyFieldDateComponent ui onChange', event);
    let targetValue = event?.target?.value;
    let utcDate: string;
    switch (this.field?.props?.type) {
      case 'datetime-local':
        const localDate = new Date(targetValue || Date.now()); // this is interpreted as local date time
        utcDate = localDate.toISOString();
        this.formControl.setValue(utcDate); 
        break;
      case 'date':
        this.formControl.setValue(targetValue); // yyyy-mm-dd
        break;
      case 'time':
        const now = new Date();
        const [hours, minutes] = (targetValue || '00:00').split(':').map((part: string) => parseInt(part, 10));
        now.setHours(hours, minutes, 0, 0);
        utcDate = now.toISOString();
        const utcTime = utcDate.split('T')[1];
        this.formControl.setValue(utcTime); // HH:MM:SS.XXXZ in UTC
        break;
      default:
        console.warn(`FormlyFieldDateTimeComponent: Unsupported type ${this.field?.props?.type}`);
    }
    super.onChange(event);
    this.localValue = targetValue;
    this.changeDetectorRef.markForCheck();
  }
}
